/**
 * Table Card Component
 * Reusable card displaying table information
 */

import React from 'react';
import { motion } from 'framer-motion';
import { anteRise } from './tableSpeeds';

export interface Table {
  id: string;
  name: string;
  maxPlayers: number;
  currentPlayers: number;
  anteAmount: number;
  anteIncrease?: number;
  anteGrowth?: string;
  anteEvery?: number;
  minBuyIn: number;
  maxBuyIn: number;
  status: 'WAITING' | 'ACTIVE' | 'PAUSED' | 'CLOSED';
  isPrivate: boolean;
  /** Who's seated — bots keep seats warm and make way for a person joining. */
  players?: Array<{ isBot?: boolean }>;
}

interface TableCardProps {
  table: Table;
  onJoin?: (tableId: string) => void;
  onSpectate?: (tableId: string) => void;
}

const TableCard: React.FC<TableCardProps> = ({ table, onJoin, onSpectate }) => {

  const getStatusColor = () => {
    switch (table.status) {
      case 'ACTIVE':
        return 'bg-green-500';
      case 'WAITING':
        return 'bg-yellow-500';
      case 'PAUSED':
        return 'bg-orange-500';
      case 'CLOSED':
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusText = () => {
    switch (table.status) {
      case 'ACTIVE':
        return 'In Progress';
      case 'WAITING':
        return 'Waiting for Players';
      case 'PAUSED':
        return 'Paused';
      case 'CLOSED':
      default:
        return 'Closed';
    }
  };

  const formatChips = (chips: number): string => {
    if (chips >= 1000000) return `${(chips / 1000000).toFixed(1)}M`;
    if (chips >= 1000) return `${(chips / 1000).toFixed(1)}K`;
    return chips.toString();
  };

  // Bots make way for people, so only people fill a table — and you can
  // join between hands at any point, not just before the first.
  const bots = (table.players ?? []).filter((p) => p.isBot).length;
  const people = table.currentPlayers - bots;
  const isFull = people >= table.maxPlayers;
  const canJoin = table.status !== 'CLOSED' && !isFull;

  return (
    <motion.div
      className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border-2 border-gray-700 hover:border-yellow-600 shadow-lg hover:shadow-yellow-600/20 transition-all overflow-hidden"
      whileHover={{ scale: 1.02, y: -4 }}
      transition={{ duration: 0.2 }}
    >
      {/* Status Bar */}
      <div className={`h-2 ${table.status === 'ACTIVE' ? 'bg-green-500' : 'bg-yellow-500'}`} />

      <div className="p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-3 sm:mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-xl font-bold text-white truncate">{table.name}</h3>
              {table.isPrivate && (
                <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">
                  🔒 Private
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <span className={`inline-block w-2 h-2 rounded-full ${getStatusColor()} animate-pulse`} />
              <span className="text-sm text-gray-400">{getStatusText()}</span>
              {/* Phones: the chips each here, instead of their own box. */}
              <span className="text-sm text-gray-500 sm:hidden">· {formatChips(table.minBuyIn)} chips each</span>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
          {/* Players */}
          <div className="bg-gray-900/50 rounded-lg p-3">
            <div className="text-xs text-gray-400 mb-1">Players</div>
            <div className="text-lg font-bold text-white">
              {people} / {table.maxPlayers}
              {bots > 0 && <span className="ml-1.5 text-xs font-normal text-gray-400">+ {bots} bots</span>}
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
              <div
                className={`h-2 rounded-full transition-all ${
                  isFull ? 'bg-red-500' : 'bg-green-500'
                }`}
                style={{ width: `${(people / table.maxPlayers) * 100}%` }}
              />
            </div>
          </div>

          {/* Ante */}
          <div className="bg-gray-900/50 rounded-lg p-3">
            <div className="text-xs text-gray-400 mb-1">Ante</div>
            <div className="text-lg font-bold text-yellow-400">
              {formatChips(table.anteAmount)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {/* The table's speed: how the ante rises (or the buy-in, for a fixed ante). */}
              {anteRise(table) ?? `Min buy-in: ${formatChips(table.minBuyIn)}`}
            </div>
          </div>
        </div>

        {/* Buy-in Range */}
        <div className="hidden sm:block bg-gray-900/50 rounded-lg p-3 mb-4">
          <div className="flex justify-between items-center">
            <div>
              <div className="text-xs text-gray-400">{table.minBuyIn === table.maxBuyIn ? 'Chips each' : 'Buy-in Range'}</div>
              <div className="text-sm font-bold text-white mt-1">
                {table.minBuyIn === table.maxBuyIn
                  ? formatChips(table.minBuyIn)
                  : `${formatChips(table.minBuyIn)} - ${formatChips(table.maxBuyIn)}`}
              </div>
            </div>
            <div className="text-2xl">💰</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <motion.button
            onClick={() => onJoin?.(table.id)}
            disabled={!canJoin}
            className={`flex-1 px-4 py-3 rounded-lg font-bold transition-all ${
              canJoin
                ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white'
                : 'bg-gray-700 text-gray-500 cursor-not-allowed'
            }`}
            whileHover={canJoin ? { scale: 1.05 } : {}}
            whileTap={canJoin ? { scale: 0.95 } : {}}
          >
            {isFull ? 'Full' : canJoin ? 'Join' : 'Closed'}
          </motion.button>

          <motion.button
            onClick={() => onSpectate?.(table.id)}
            className="px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-bold transition-all"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            👁️ Spectate
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default TableCard;
